import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Radar, GitCompareArrows, Sparkles, AlertTriangle, ChevronRight, Calculator, ServerCog, Bell, FileText, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import DomainCard from "@/components/DomainCard";
import ScoreGauge from "@/components/ScoreGauge";
import EmergentBuildPromo from "@/components/EmergentBuildPromo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const SAMPLES = ["linkwebsitepro.linklitepro.com", "example.com"];
const SAMPLE_SCORES = [
  { label: "Security", value: 60 },
  { label: "SEO", value: 75 },
  { label: "Readiness", value: 74 },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { plan, isAdmin } = useAuth();
  const [input, setInput] = useState("");
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState([]);
  const [alertCounts, setAlertCounts] = useState({ critical: 0, warning: 0, info: 0, unread: 0 });

  const loadAlerts = useCallback(async () => {
    try {
      const res = await api.get("/changes");
      const ch = (res.data.changes || []).filter((c) => !c.read);
      setAlertCounts({
        critical: ch.filter((c) => c.severity === "critical").length,
        warning: ch.filter((c) => c.severity === "warning").length,
        info: ch.filter((c) => c.severity === "info").length,
        unread: res.data.unread || 0,
      });
    } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/domains");
      setDomains(res.data.domains || []);
    } catch {
      toast.error("Failed to load domains");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadAlerts(); }, [load, loadAlerts]);

  // Handle return from Stripe checkout (single or bulk) — poll then reload.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0, cancelled = false;
    const clean = () => window.history.replaceState({}, "", "/dashboard");
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") { toast.success("Payment complete — your reports are unlocked!"); clean(); load(); return; }
        if (r.data.status === "expired") { toast.error("Payment session expired."); clean(); return; }
      } catch {}
      if (tries++ < 6) setTimeout(poll, 2000); else clean();
    };
    poll();
    return () => { cancelled = true; };
  }, [load]);

  const bulkUnlock = async (list) => {
    try {
      const res = await api.post("/checkout/bulk", { domains: list, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
      else toast.error("Could not start checkout");
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
  };

  const addToInput = (domain) => {
    setInput((prev) => {
      const parts = prev.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      if (parts.includes(domain)) return prev;
      return [...parts, domain].join("\n");
    });
  };

  const scan = async () => {
    const list = input.split(/[\s,]+/).map((d) => d.trim()).filter(Boolean);
    if (!list.length) { toast.error("Enter at least one domain"); return; }
    setScanning(true);
    try {
      const res = await api.post("/scan/batch", { domains: list });
      const results = res.data.results || [];
      const scanned = results.filter((r) => !r.error).length;
      const blocked = res.data.blocked || 0;
      if (scanned > 0) toast.success(`Scanned ${scanned} domain${scanned > 1 ? "s" : ""}`);
      if (blocked > 0) toast.warning(`${blocked} domain${blocked > 1 ? "s" : ""} not scanned — plan limit reached. Remove a domain or upgrade.`);
      if (scanned === 0 && blocked === 0) toast.error("No valid domains found");
      setInput("");
      await load();
    } catch {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const unlockReport = async (domain) => {
    try {
      const res = await api.post("/checkout/product", { product_id: "ai_report", domain, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
      else toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  const remove = async (domain) => {
    try {
      await api.delete(`/domains/${encodeURIComponent(domain)}`);
      setDomains((d) => d.filter((x) => x.domain !== domain));
      setSelected((s) => s.filter((x) => x !== domain));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const toggleSelect = (domain) => {
    setSelected((s) => (s.includes(domain) ? s.filter((x) => x !== domain) : [...s, domain]));
  };

  const avg = (key) => {
    const vals = domains.map((d) => d.last_result?.scores?.[key]).filter((v) => v != null);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : "--";
  };

  const openReports = () => {
    if (domains.length) navigate(`/domain/${encodeURIComponent(domains[0].domain)}`);
    else toast.message("Scan a domain first to generate downloadable reports.");
  };

  const isFreeUser = plan === "free" && !isAdmin;
  const selectedLockedDomains = isFreeUser
    ? selected.filter((dn) => { const d = domains.find((x) => x.domain === dn); return d && !d.report_unlocked; })
    : [];
  const bulkPct = selectedLockedDomains.length >= 5 ? 25 : selectedLockedDomains.length >= 3 ? 20 : 0;
  const bulkTotal = (29 * selectedLockedDomains.length * (1 - bulkPct / 100)).toFixed(2);

  const tiles = [
    { key: "savings", title: "Savings Calculator", desc: "Estimate migration savings", icon: Calculator, onClick: () => navigate("/migration"), gradient: true, testid: "quick-action-savings" },
    { key: "backend", title: "Backend Scan", desc: "Dependency & CVE audit", icon: ServerCog, onClick: () => navigate("/backend-scan"), testid: "quick-action-backend" },
    { key: "compare", title: "Compare", desc: "Sites side by side", icon: GitCompareArrows, onClick: () => navigate("/compare"), testid: "quick-action-compare" },
    { key: "alerts", title: "Alerts", desc: "Track changes nightly", icon: Bell, onClick: () => navigate("/changes"), testid: "quick-action-alerts", badge: alertCounts.unread },
    { key: "reports", title: "Reports", desc: "Export PDF & JSON", icon: FileText, onClick: openReports, testid: "quick-action-reports" },
  ];

  return (
    <Layout>
      {/* Welcome */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[#4da3ff] mb-2">
          <Radar size={16} />
          <span className="text-[11px] font-bold uppercase tracking-[0.25em]">Command Center</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
          Scan, audit &amp; <span className="brand-gradient">optimize</span> any website
        </h1>
        <p className="text-zinc-400 mt-2 max-w-2xl text-sm sm:text-base">
          Tech stack, security, SEO, privacy &amp; detected services — with 0–100 scores, fixes, and an AI build brief.
        </p>
      </div>

      {/* Alert strip */}
      {alertCounts.unread > 0 && (
        <button
          data-testid="alert-summary-strip"
          onClick={() => navigate("/changes")}
          className="w-full flex items-center gap-4 flex-wrap bg-[#121212] border border-white/10 hover:border-white/25 rounded-xl px-5 py-4 mb-6 transition-colors text-left group"
        >
          <div className="w-9 h-9 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center shrink-0">
            <AlertTriangle size={17} className="text-[#FF3B30]" />
          </div>
          <div className="flex items-center gap-4 flex-wrap flex-1">
            <span className="text-white text-sm font-medium">
              {alertCounts.unread} unacknowledged change{alertCounts.unread > 1 ? "s" : ""} across your domains
            </span>
            <div className="flex items-center gap-3 font-mono text-xs">
              {alertCounts.critical > 0 && <span className="text-[#FF3B30]">● {alertCounts.critical} critical</span>}
              {alertCounts.warning > 0 && <span className="text-[#FFD60A]">● {alertCounts.warning} warning</span>}
              {alertCounts.info > 0 && <span className="text-[#4da3ff]">● {alertCounts.info} info</span>}
            </div>
          </div>
          <span className="text-zinc-400 group-hover:text-white text-sm flex items-center gap-1 transition-colors">
            View alerts <ChevronRight size={15} />
          </span>
        </button>
      )}

      {/* Bento: scanner + side panel */}
      <div className="grid grid-cols-12 gap-5 mb-6">
        {/* Scanner (hero) */}
        <div className="col-span-12 lg:col-span-7 bg-[#121212] border border-white/10 rounded-xl p-6 sm:p-7 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#007AFF]/10 blur-3xl pointer-events-none" />
          <div className="flex items-center gap-2 mb-1 relative">
            <Search size={18} className="text-[#4da3ff]" />
            <h2 className="font-heading text-lg font-semibold text-white">New Scan</h2>
          </div>
          <p className="text-zinc-500 text-sm mb-4 relative">Enter one or more domains — separate with a comma, space, or new line.</p>
          <textarea
            data-testid="domain-input"
            className="w-full bg-black border border-white/15 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] outline-none font-mono text-sm transition-all resize-y relative"
            rows={4}
            autoCapitalize="none" autoCorrect="off" autoComplete="off" spellCheck={false} inputMode="url"
            placeholder={"example.com, anotherdomain.org"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <div className="mt-4">
            <button
              data-testid="scan-domains-btn"
              onClick={scan}
              disabled={scanning}
              className="btn-brand disabled:opacity-50 text-white rounded-lg px-5 py-2.5 font-medium transition-all active:scale-95 flex items-center gap-2 shadow-[0_0_18px_rgba(155,92,255,0.25)]"
            >
              {scanning ? <><Loader2 size={16} className="animate-spin" /> Scanning…</> : <><Search size={16} /> Scan Domains</>}
            </button>
          </div>
          {domains.length === 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="sample-domains">
            <span className="text-xs text-zinc-500 mr-1">Try a sample:</span>
            {SAMPLES.map((s) => (
              <button key={s} data-testid={`sample-domain-${s}`} onClick={() => addToInput(s)}
                className="text-xs font-mono text-[#4da3ff] bg-[#007AFF]/10 border border-[#007AFF]/30 hover:bg-[#007AFF]/20 rounded-full px-3 py-1 transition-colors">
                {s}
              </button>
            ))}
          </div>
          )}
          {domains.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="recent-domains">
              <span className="text-xs text-zinc-500 mr-1">Recent:</span>
              {domains.slice(0, 6).map((d) => (
                <button key={d.domain} data-testid={`recent-domain-${d.domain}`} onClick={() => addToInput(d.domain)}
                  className="text-xs font-mono text-zinc-300 bg-white/5 border border-white/10 hover:border-[#007AFF]/50 hover:text-white rounded-full px-3 py-1 transition-colors">
                  {d.domain}
                </button>
              ))}
            </div>
          )}
          <p className="text-[11px] text-zinc-600 mt-3 leading-relaxed">
            Only scan websites you own or are authorized to test. See our{" "}
            <a href="/legal/acceptable-use.html" target="_blank" rel="noreferrer" className="underline hover:text-zinc-400">Acceptable Use Policy</a>.
          </p>
        </div>

        {/* Side panel: sample preview + quick actions */}
        <div className="col-span-12 lg:col-span-5 flex flex-col gap-4">
          {/* Sample report preview — "sell it" (only before the first scan) */}
          {domains.length === 0 && (
          <div className="bg-[#121212] border border-white/10 rounded-xl p-5 relative overflow-hidden" data-testid="sample-preview">
            <div className="absolute inset-0 bg-gradient-to-br from-[#007AFF]/8 to-[#9b5cff]/8 pointer-events-none" />
            <div className="flex items-center justify-between relative mb-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">What every scan reveals</span>
              <span className="text-[10px] font-mono text-white/40 border border-white/10 rounded px-1.5 py-0.5">EXAMPLE</span>
            </div>
            <div className="flex items-center justify-around relative">
              {SAMPLE_SCORES.map((s) => (
                <ScoreGauge key={s.label} value={s.value} size={62} stroke={6} label={s.label} />
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4 relative">
              {["Tech stack", "SSL/TLS", "CSP", "Meta & OG", "Trackers", "EOL software"].map((t) => (
                <span key={t} className="text-[10px] text-zinc-400 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">{t}</span>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 relative">Full report includes 6 scores, prioritized fixes &amp; an AI build brief.</p>
          </div>
          )}

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-4">
            {tiles.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={t.testid}
                  onClick={t.onClick}
                  className={`group text-left rounded-xl p-4 transition-all duration-300 hover:-translate-y-1 ${
                    t.gradient
                      ? "col-span-2 bg-gradient-to-br from-[#007AFF]/15 to-[#9b5cff]/15 border border-[#007AFF]/30 hover:border-[#007AFF]/60"
                      : "col-span-1 bg-[#121212] border border-white/10 hover:border-white/25 hover:bg-[#171717]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                      <Icon size={17} className="text-[#4da3ff]" />
                    </div>
                    {t.badge > 0 && <span className="text-[10px] font-bold bg-[#FF3B30] text-white rounded-full px-1.5 py-0.5">{t.badge}</span>}
                    <ArrowRight size={15} className="text-white/30 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all ml-auto" />
                  </div>
                  <p className="text-white font-medium text-sm mt-3">{t.title}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{t.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Stats strip */}
      {domains.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Domains Tracked", val: domains.length, testid: "stat-domains-tracked" },
            { label: "Avg Security", val: avg("security"), testid: "stat-security-score", gradient: true },
            { label: "Avg Readiness", val: avg("integration"), testid: "stat-readiness" },
            { label: "Avg Frontend", val: avg("frontend"), testid: "stat-frontend" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-[#121212] border border-white/10 rounded-xl p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
              <p className={`font-heading text-3xl font-bold mt-1 ${s.gradient ? "brand-gradient" : "text-white"}`}>{s.val}</p>
            </div>
          ))}
        </div>
      )}

      {/* Selection bar */}
      {selected.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-lg px-4 py-3 mb-6">
          <span className="text-sm text-white font-mono">{selected.length} selected{selectedLockedDomains.length >= 2 ? ` · ${selectedLockedDomains.length} locked` : ""}</span>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedLockedDomains.length >= 2 && (
              <button
                data-testid="bulk-unlock-btn"
                onClick={() => bulkUnlock(selectedLockedDomains)}
                className="btn-brand text-white rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Lock size={15} /> Unlock {selectedLockedDomains.length} reports — ${bulkTotal}{bulkPct ? ` · save ${bulkPct}%` : ""}
              </button>
            )}
            <button
              data-testid="compare-selected-btn"
              onClick={() => navigate(`/compare?domains=${selected.map(encodeURIComponent).join(",")}`)}
              className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
            >
              <GitCompareArrows size={15} /> Compare selected
            </button>
          </div>
        </div>
      )}

      {/* Recent scans */}
      {domains.length > 0 && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold text-white">Recent Scans</h2>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-zinc-500"><Loader2 size={24} className="animate-spin" /></div>
      ) : domains.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-xl" data-testid="empty-state">
          <Radar size={40} className="text-zinc-700 mx-auto" />
          <p className="text-zinc-400 mt-4">No domains scanned yet.</p>
          <p className="text-zinc-600 text-sm mt-1">Paste a domain above and hit Scan — or try a sample.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {domains.map((d) => (
            <DomainCard key={d.domain} item={d} onDelete={remove} selected={selected.includes(d.domain)} onToggleSelect={toggleSelect} locked={plan === "free" && !isAdmin && !d.report_unlocked} onUnlock={unlockReport} />
          ))}
        </div>
      )}

      {/* Build handoff promo */}
      <div className="mt-8 mb-6">
        <EmergentBuildPromo />
      </div>

      {/* Upgrade CTA */}
      {plan === "free" && (
        <button
          data-testid="upgrade-cta-banner"
          onClick={() => navigate("/pricing")}
          className="w-full flex items-center gap-4 flex-wrap rounded-xl px-5 py-4 mb-6 text-left group border border-[#007AFF]/30 bg-gradient-to-r from-[#007AFF]/12 to-[#9b5cff]/12 hover:from-[#007AFF]/20 hover:to-[#9b5cff]/20 transition-colors"
        >
          <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
            <Sparkles size={17} className="text-[#4da3ff]" />
          </div>
          <div className="flex-1">
            <span className="text-white text-sm font-semibold">Unlock the full platform</span>
            <p className="text-zinc-400 text-sm mt-0.5">Unlimited domains, AI build briefs, nightly re-scans, alerts and downloadable reports.</p>
          </div>
          <span className="brand-gradient font-medium text-sm flex items-center gap-1">See what Pro unlocks <ChevronRight size={15} /></span>
        </button>
      )}
    </Layout>
  );
}
